const translations = {
  en: {
    // Subjects
    subjects: {
      'Math': 'Math',
      'Science': 'Science',
      'Social Studies': 'Social Studies',
      'Language Arts': 'Language Arts',
    },
    // Topics
    topics: {
      'Number Operations': 'Number Operations',
      'Algebra': 'Algebra',
      'Geometry': 'Geometry',
      'Statistics & Probability': 'Statistics & Probability',
      'Ratios & Proportions': 'Ratios & Proportions',
      'Functions': 'Functions',
      'Data Analysis': 'Data Analysis',
      'Life Science': 'Life Science',
      'Earth Science': 'Earth Science',
      'Physical Science': 'Physical Science',
      'Energy & Waves': 'Energy & Waves',
      'Ecosystems': 'Ecosystems',
      'Human Body Systems': 'Human Body Systems',
      'Scientific Reasoning': 'Scientific Reasoning',
      'US History': 'US History',
      'World History': 'World History',
      'Civics & Government': 'Civics & Government',
      'Economics': 'Economics',
      'Geography': 'Geography',
      'US Constitution': 'US Constitution',
      'Reading Comprehension': 'Reading Comprehension',
      'Grammar & Usage': 'Grammar & Usage',
      'Vocabulary in Context': 'Vocabulary in Context',
      'Argument Analysis': 'Argument Analysis',
      'Informational Texts': 'Informational Texts',
      'Extended Response': 'Extended Response',
    },
    // Sidebar
    study: 'Study',
    progress: 'Progress',
    mockExam: 'Mock Exam',
    aiTutor: 'AI Tutor',
    essayPractice: 'Essay Practice',
    leaderboard: 'Leaderboard',
    upgrade: 'Upgrade',
    logOut: 'Log out',
    questionsLeft: (n) => `${n} / 5 questions left`,
    upgradeUnlimited: 'Upgrade for unlimited',
    remindersOn: '🔔 Reminders on',
    enableReminders: '🔔 Enable Study Reminders',
    premium: '⭐ Premium',
    streak: (n) => `🔥 ${n} day${n !== 1 ? 's' : ''} streak`,

    // Header
    chooseSubject: 'Choose a Subject',
    yourProgress: 'Your Progress',
    goPremium: 'Go Premium',
    mockGEDExam: 'Mock GED Exam',
    examResults: 'Exam Results',
    back: '← Back',

    // Home
    pageIntro: 'Select a subject below to start practicing with AI-generated questions.',
    topics: (n) => `${n} topics`,
    answered: (n) => `${n} answered`,

    // TopicPicker
    pickTopic: 'Pick a topic to practice',
    dailyLimitReached: 'Daily limit reached',
    questionsLeftToday: (n) => `${n} free question${n !== 1 ? 's' : ''} left today`,
    goPremiumBtn: 'Go Premium',
    notStarted: 'Not started',

    // TopicQuiz
    generatingQuestion: 'Generating question…',
    retry: 'Retry',
    nextQuestion: 'Next Question',
    finishSession: 'Finish Session',
    sessionComplete: 'Session Complete',
    keepItUp: "Keep practicing — you're getting there!",
    greatWork: 'Great work! Keep it up.',
    backToTopics: 'Back to Topics',
    showHint: 'Show Hint',
    hideHint: 'Hide Hint',
    askAITutor: 'Ask AI Tutor about this',
    reportIncorrect: '⚑ Report incorrect answer',
    reported: '⚑ Reported — thanks!',

    // MockExam
    startExam: 'Start Exam',
    continueExam: 'Continue →',
    nextSubject: 'Next Subject →',
    continueToEssay: 'Continue to Essay →',
    seeResults: 'See Results →',
    complete: 'Complete',
    upNext: 'Up next:',
    correct: '✓ Correct',
    correctAnswer: (a) => `✗ Correct answer: ${a}`,
    loadingQuestion: (s) => `Loading ${s} question…`,
    generatingEssay: 'Generating essay prompt…',
    gradingEssay: 'AI is grading your essay…',
    gradingNote: 'This takes about 10 seconds',
    submitEssay: 'Submit Essay',
    minWords: (n) => `(min ${n})`,
    previewComplete: 'Preview Complete!',
    preview2Questions: 'You answered 2 preview questions. Upgrade to unlock the full 40-question mock exam with AI essay grading.',
    unlockFullExam: 'Unlock Full Exam — Upgrade',
    examIntroDesc: 'Simulate real GED testing conditions. 40 questions across all 4 subjects plus a graded essay — no hints allowed.',
    examPreviewDesc: 'Preview the mock exam with 2 questions across Math & Science. Upgrade for the full 40-question exam + essay grading.',
    upgradeFullExam: '⭐ Upgrade for the full exam →',

    // Progress
    studyProgress: 'Study Progress',
    gedReadiness: 'GED Readiness',
    unlocksAfter: (n) => `Unlocks after 10 questions (${n}/10)`,
    accuracyAll: (n) => `${n}% accuracy across all topics`,
    completeQuiz: 'Complete a quiz to see your study progress here.',
    mockExamHistory: 'Mock Exam History',
    noExams: 'No mock exams completed yet.',
    inProgress: 'In Progress',

    // Tutor
    tutorGreeting: "Hi! I'm your GED tutor. Ask me anything about Math, Science, Social Studies, or Language Arts.",
    tutorContext: (topic, subject) => `I see you're working on **${topic}** in ${subject}. What would you like me to explain?`,
    tutorError: 'Sorry, I had trouble connecting. Please try again.',
    askAnything: 'Ask anything…',
    send: 'Send',

    // Essay Practice
    essayPracticeTitle: 'Essay Practice',
    essayPracticeDesc: 'Get a real GED-style passage and writing prompt. Write your response, then receive instant AI feedback scored on the official GED rubric.',
    scoredOn: 'Scored on 3 traits (0–2 each, 6 points total):',
    generatePrompt: 'Generate Essay Prompt',
    submitForGrading: 'Submit for Grading',
    tryAnother: 'Try Another Prompt',
    reviseEssay: 'Revise My Essay',
    extendedResponse: 'Extended Response Practice',
    readPassage: 'Read the following passage:',
    essayScore: 'Essay Score',
    minWordsHint: (n) => `${n} words (min 50)`,
    wordsOk: (n) => `${n} words ✓`,

    // Onboarding
    onboarding: [
      { icon: '🎓', title: 'Welcome to GEDReady Prep', body: 'Your path to GED success starts here. We generate AI-powered practice questions across all 4 GED subjects — tailored to the real exam.' },
      { icon: '📚', title: 'How it works', body: 'Pick a subject, choose a topic, and practice with unlimited AI-generated questions. Get instant explanations for every answer — right or wrong.' },
      { icon: '🤖', title: 'Your personal AI Tutor', body: "Stuck on a question? Ask the AI Tutor for a deeper explanation. Take a full mock exam with essay grading when you're ready to test yourself." },
      { icon: '🔥', title: "You've got this", body: "Start with 5 free questions today. Build your streak, track your progress, and unlock unlimited access when you're ready to go all in." },
    ],
    letsStart: "Let's Start! →",
    next: 'Next →',
    skip: 'Skip',

    // Upgrade
    upgradeTitle: 'Your GED is closer than you think',
    upgradeSub: 'Unlock unlimited practice, a personal AI Tutor, and full mock exams — everything you need to pass with confidence.',
    bestValue: 'Best Value — Save 40%',
    yearlyPlan: 'Yearly Plan',
    monthlyPlan: 'Monthly Plan',
    yearlyNote: '$5.99/mo when billed annually · $71.88/year',
    monthlyNote: 'Cancel anytime · No commitment',
    startYearly: 'Start Yearly — Best Value',
    startMonthly: 'Start Monthly — $9.99',
    whyUpgrade: 'Why upgrade?',
    upgradeFeatures: [
      '✓ Unlimited AI-generated questions — never run out of practice',
      '✓ Personal AI Tutor explains every wrong answer instantly',
      '✓ Full 40-question mock exam + AI essay grading',
      '✓ All 26 topics across all 4 GED subjects',
      '✓ Detailed progress tracking across every topic',
    ],
    upgradeObjection: 'The GED can increase your earning potential by $9,000+ per year. At $9.99/month, that\'s a return of over 75× in your first year alone.',
    maybeLater: 'Continue with 5 free questions/day',
    redirecting: 'Redirecting…',
    betaCode: 'Have a beta access code?',
    enterCode: 'Enter code',
    apply: 'Apply',
    premiumUnlocked: '✓ Premium unlocked! Reloading…',
    pleaseLogin: 'Please log in first.',

    // Auth
    logIn: 'Log In',
    signUp: 'Sign Up',
    forgotPassword: 'Forgot Password',
    emailPlaceholder: 'Email',
    passwordPlaceholder: 'Password',
    continueWithGoogle: 'Continue with Google',
    sendResetLink: 'Send Reset Link',
    checkEmail: 'Check your email for a reset link.',
    backToLogin: '← Back to login',
    alreadyHaveAccount: 'Already have an account?',
    noAccount: "Don't have an account?",
  },

  es: {
    // Subjects
    subjects: {
      'Math': 'Matemáticas',
      'Science': 'Ciencias',
      'Social Studies': 'Estudios Sociales',
      'Language Arts': 'Artes del Lenguaje',
    },
    // Topics
    topics: {
      'Number Operations': 'Operaciones Numéricas',
      'Algebra': 'Álgebra',
      'Geometry': 'Geometría',
      'Statistics & Probability': 'Estadística y Probabilidad',
      'Ratios & Proportions': 'Razones y Proporciones',
      'Functions': 'Funciones',
      'Data Analysis': 'Análisis de Datos',
      'Life Science': 'Ciencias de la Vida',
      'Earth Science': 'Ciencias de la Tierra',
      'Physical Science': 'Ciencias Físicas',
      'Energy & Waves': 'Energía y Ondas',
      'Ecosystems': 'Ecosistemas',
      'Human Body Systems': 'Sistemas del Cuerpo Humano',
      'Scientific Reasoning': 'Razonamiento Científico',
      'US History': 'Historia de EE.UU.',
      'World History': 'Historia Mundial',
      'Civics & Government': 'Cívica y Gobierno',
      'Economics': 'Economía',
      'Geography': 'Geografía',
      'US Constitution': 'Constitución de EE.UU.',
      'Reading Comprehension': 'Comprensión Lectora',
      'Grammar & Usage': 'Gramática y Uso',
      'Vocabulary in Context': 'Vocabulario en Contexto',
      'Argument Analysis': 'Análisis de Argumentos',
      'Informational Texts': 'Textos Informativos',
      'Extended Response': 'Respuesta Extendida',
    },
    // Sidebar
    study: 'Estudiar',
    progress: 'Progreso',
    mockExam: 'Examen de Práctica',
    aiTutor: 'Tutor IA',
    essayPractice: 'Práctica de Ensayo',
    leaderboard: 'Tabla de Líderes',
    upgrade: 'Mejorar Plan',
    logOut: 'Cerrar sesión',
    questionsLeft: (n) => `${n} / 5 preguntas restantes`,
    upgradeUnlimited: 'Mejora para acceso ilimitado',
    remindersOn: '🔔 Recordatorios activos',
    enableReminders: '🔔 Activar recordatorios de estudio',
    premium: '⭐ Premium',
    streak: (n) => `🔥 ${n} día${n !== 1 ? 's' : ''} seguido${n !== 1 ? 's' : ''}`,

    // Header
    chooseSubject: 'Elige una Materia',
    yourProgress: 'Tu Progreso',
    goPremium: 'Hazte Premium',
    mockGEDExam: 'Examen GED de Práctica',
    examResults: 'Resultados del Examen',
    back: '← Volver',

    // Home
    pageIntro: 'Selecciona una materia para practicar con preguntas generadas por IA.',
    topics: (n) => `${n} temas`,
    answered: (n) => `${n} respondidas`,

    // TopicPicker
    pickTopic: 'Elige un tema para practicar',
    dailyLimitReached: 'Límite diario alcanzado',
    questionsLeftToday: (n) => `${n} pregunta${n !== 1 ? 's' : ''} gratis restante${n !== 1 ? 's' : ''} hoy`,
    goPremiumBtn: 'Hazte Premium',
    notStarted: 'No iniciado',

    // TopicQuiz
    generatingQuestion: 'Generando pregunta…',
    retry: 'Reintentar',
    nextQuestion: 'Siguiente Pregunta',
    finishSession: 'Terminar Sesión',
    sessionComplete: 'Sesión Completa',
    keepItUp: '¡Sigue practicando — vas mejorando!',
    greatWork: '¡Excelente trabajo! Sigue así.',
    backToTopics: 'Volver a Temas',
    showHint: 'Mostrar Pista',
    hideHint: 'Ocultar Pista',
    askAITutor: 'Preguntarle al Tutor IA',
    reportIncorrect: '⚑ Reportar respuesta incorrecta',
    reported: '⚑ Reportado — ¡gracias!',

    // MockExam
    startExam: 'Comenzar Examen',
    continueExam: 'Continuar →',
    nextSubject: 'Siguiente Materia →',
    continueToEssay: 'Continuar al Ensayo →',
    seeResults: 'Ver Resultados →',
    complete: 'Completado',
    upNext: 'A continuación:',
    correct: '✓ Correcto',
    correctAnswer: (a) => `✗ Respuesta correcta: ${a}`,
    loadingQuestion: (s) => `Cargando pregunta de ${s}…`,
    generatingEssay: 'Generando tema del ensayo…',
    gradingEssay: 'La IA está calificando tu ensayo…',
    gradingNote: 'Esto tarda unos 10 segundos',
    submitEssay: 'Enviar Ensayo',
    minWords: (n) => `(mín. ${n})`,
    previewComplete: '¡Vista previa completa!',
    preview2Questions: 'Respondiste 2 preguntas de vista previa. Mejora tu plan para el examen completo de 40 preguntas con calificación de ensayo.',
    unlockFullExam: 'Desbloquear Examen Completo',
    examIntroDesc: 'Simula las condiciones reales del GED. 40 preguntas en las 4 materias más un ensayo calificado — sin pistas.',
    examPreviewDesc: 'Vista previa con 2 preguntas de Matemáticas y Ciencias. Mejora para el examen completo de 40 preguntas + ensayo.',
    upgradeFullExam: '⭐ Mejora para el examen completo →',

    // Progress
    studyProgress: 'Progreso de Estudio',
    gedReadiness: 'Preparación para el GED',
    unlocksAfter: (n) => `Se desbloquea después de 10 preguntas (${n}/10)`,
    accuracyAll: (n) => `${n}% de precisión en todos los temas`,
    completeQuiz: 'Completa un quiz para ver tu progreso aquí.',
    mockExamHistory: 'Historial de Exámenes',
    noExams: 'Aún no has completado ningún examen.',
    inProgress: 'En Progreso',

    // Tutor
    tutorGreeting: '¡Hola! Soy tu tutor del GED. Pregúntame lo que quieras sobre Matemáticas, Ciencias, Estudios Sociales o Artes del Lenguaje.',
    tutorContext: (topic, subject) => `Veo que estás trabajando en **${topic}** de ${subject}. ¿Qué quieres que te explique?`,
    tutorError: 'Lo siento, tuve problemas para conectarme. Por favor intenta de nuevo.',
    askAnything: 'Pregunta lo que quieras…',
    send: 'Enviar',

    // Essay Practice
    essayPracticeTitle: 'Práctica de Ensayo',
    essayPracticeDesc: 'Recibe un pasaje y tema de escritura estilo GED real. Escribe tu respuesta y recibe retroalimentación instantánea de IA según el rúbrico oficial del GED.',
    scoredOn: 'Puntuado en 3 criterios (0–2 cada uno, 6 puntos en total):',
    generatePrompt: 'Generar Tema de Ensayo',
    submitForGrading: 'Enviar para Calificación',
    tryAnother: 'Intentar Otro Tema',
    reviseEssay: 'Revisar Mi Ensayo',
    extendedResponse: 'Práctica de Respuesta Extendida',
    readPassage: 'Lee el siguiente pasaje:',
    essayScore: 'Puntuación del Ensayo',
    minWordsHint: (n) => `${n} palabras (mín. 50)`,
    wordsOk: (n) => `${n} palabras ✓`,

    // Onboarding
    onboarding: [
      { icon: '🎓', title: 'Bienvenido a GEDReady Prep', body: 'Tu camino al éxito en el GED comienza aquí. Generamos preguntas de práctica con IA para las 4 materias del GED — adaptadas al examen real.' },
      { icon: '📚', title: 'Cómo funciona', body: 'Elige una materia, selecciona un tema y practica con preguntas ilimitadas generadas por IA. Obtén explicaciones instantáneas para cada respuesta.' },
      { icon: '🤖', title: 'Tu tutor personal de IA', body: '¿Atascado en una pregunta? Pídele al Tutor IA una explicación más detallada. Haz el examen completo con calificación de ensayo cuando estés listo.' },
      { icon: '🔥', title: '¡Tú puedes lograrlo!', body: 'Comienza con 5 preguntas gratis hoy. Construye tu racha, rastrea tu progreso y desbloquea acceso ilimitado cuando estés listo.' },
    ],
    letsStart: '¡Empezar! →',
    next: 'Siguiente →',
    skip: 'Omitir',

    // Upgrade
    upgradeTitle: 'Tu GED está más cerca de lo que crees',
    upgradeSub: 'Desbloquea práctica ilimitada, un Tutor IA personal y exámenes completos — todo lo que necesitas para pasar con confianza.',
    bestValue: 'Mejor Valor — Ahorra 40%',
    yearlyPlan: 'Plan Anual',
    monthlyPlan: 'Plan Mensual',
    yearlyNote: '$5.99/mes facturado anualmente · $71.88/año',
    monthlyNote: 'Cancela cuando quieras · Sin compromiso',
    startYearly: 'Comenzar Anual — Mejor Valor',
    startMonthly: 'Comenzar Mensual — $9.99',
    whyUpgrade: '¿Por qué mejorar?',
    upgradeFeatures: [
      '✓ Preguntas ilimitadas generadas por IA — nunca te quedas sin práctica',
      '✓ Tutor IA personal explica cada respuesta incorrecta al instante',
      '✓ Examen completo de 40 preguntas + calificación de ensayo con IA',
      '✓ Los 26 temas en las 4 materias del GED',
      '✓ Seguimiento detallado de progreso en cada tema',
    ],
    upgradeObjection: 'El GED puede aumentar tu potencial de ingresos en más de $9,000 por año. A $9.99/mes, eso es un retorno de más de 75× en tu primer año.',
    maybeLater: 'Continuar con 5 preguntas gratis/día',
    redirecting: 'Redirigiendo…',
    betaCode: '¿Tienes un código de acceso beta?',
    enterCode: 'Ingresar código',
    apply: 'Aplicar',
    premiumUnlocked: '✓ ¡Premium desbloqueado! Recargando…',
    pleaseLogin: 'Por favor inicia sesión primero.',

    // Auth
    logIn: 'Iniciar Sesión',
    signUp: 'Registrarse',
    forgotPassword: 'Olvidé mi contraseña',
    emailPlaceholder: 'Correo electrónico',
    passwordPlaceholder: 'Contraseña',
    continueWithGoogle: 'Continuar con Google',
    sendResetLink: 'Enviar enlace de restablecimiento',
    checkEmail: 'Revisa tu correo para el enlace de restablecimiento.',
    backToLogin: '← Volver al inicio de sesión',
    alreadyHaveAccount: '¿Ya tienes una cuenta?',
    noAccount: '¿No tienes una cuenta?',
  },
};

export default translations;
